(() => {
  const q = (selector, root = document) => root.querySelector(selector);
  const qa = (selector, root = document) => [...root.querySelectorAll(selector)];

  const setDrawer = (drawer, overlay, open) => {
    if (!drawer || !overlay) return;
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    overlay.hidden = false;
    requestAnimationFrame(() => overlay.classList.toggle('is-open', open));
    document.body.classList.toggle('drawer-open', open);
    if (!open) window.setTimeout(() => { overlay.hidden = true; }, 300);
  };

  const initDrawers = () => {
    const menu = q('[data-menu-drawer]');
    const menuOverlay = q('[data-menu-overlay]');
    const cart = q('[data-cart-drawer]');
    const cartOverlay = q('[data-drawer-overlay]');

    qa('[data-menu-open]').forEach((button) => button.addEventListener('click', () => setDrawer(menu, menuOverlay, true)));
    qa('[data-menu-close], [data-menu-overlay], [data-anchor-link]').forEach((button) => button.addEventListener('click', () => setDrawer(menu, menuOverlay, false)));
    qa('[data-cart-open]').forEach((button) => button.addEventListener('click', async () => {
      await refreshCart();
      setDrawer(cart, cartOverlay, true);
    }));
    qa('[data-cart-close], [data-drawer-overlay]').forEach((button) => button.addEventListener('click', () => setDrawer(cart, cartOverlay, false)));

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      setDrawer(menu, menuOverlay, false);
      setDrawer(cart, cartOverlay, false);
    });
  };

  const money = (cents, currency = window.Shopify?.currency?.active || 'GBP') => {
    try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(cents / 100); }
    catch (_) { return `£${(cents / 100).toFixed(2)}`; }
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;' }[char]));

  const refreshCart = async () => {
    const body = q('[data-cart-body]');
    if (!body) return;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      qa('[data-cart-count]').forEach((count) => {
        count.textContent = cart.item_count;
        count.hidden = cart.item_count === 0;
      });
      const summary = q('[data-cart-summary]');
      if (summary) summary.textContent = cart.item_count ? `${cart.item_count} item${cart.item_count === 1 ? '' : 's'} in your bag` : 'Your bag is empty';
      const footer = q('[data-cart-footer]');
      if (!cart.items.length) {
        body.innerHTML = '<div class="vellyn-empty-cart"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18M16 10a4 4 0 0 1-8 0"/></svg><p>Your bag is empty</p></div>';
        if (footer) footer.hidden = true;
        return cart;
      }
      body.innerHTML = `<div class="vellyn-cart__items">${cart.items.map((item, index) => `
        <article class="vellyn-cart-item">
          ${item.image ? `<img src="${escapeHtml(item.image)}&width=160" alt="${escapeHtml(item.product_title)}" width="80" height="80">` : '<div></div>'}
          <div><a class="vellyn-cart-item__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a><div class="vellyn-cart-item__price">${money(item.final_price, cart.currency)}</div></div>
          <div class="vellyn-cart-item__actions">
            <button type="button" data-cart-change="${index + 1}" data-quantity="0" aria-label="Remove ${escapeHtml(item.product_title)}">×</button>
            <div class="vellyn-cart-item__quantity">
              <button type="button" data-cart-change="${index + 1}" data-quantity="${item.quantity - 1}" aria-label="Reduce quantity">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-cart-change="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button>
            </div>
          </div>
        </article>`).join('')}</div>`;
      if (footer) footer.hidden = false;
      const total = q('[data-cart-total]');
      if (total) total.textContent = money(cart.total_price, cart.currency);
      qa('[data-cart-change]', body).forEach((button) => button.addEventListener('click', async () => {
        button.disabled = true;
        await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(button.dataset.cartChange), quantity: Number(button.dataset.quantity) })
        });
        await refreshCart();
      }));
      return cart;
    } catch (error) {
      body.innerHTML = '<p>We could not load your bag. Please refresh and try again.</p>';
    }
  };

  const initHero = () => {
    qa('[data-slider]').forEach((slider) => {
      const slides = qa('[data-slide]', slider);
      const dots = qa('[data-slide-to]', slider);
      if (slides.length < 2) return;
      let current = 0;
      let timer;
      let animating = false;
      const transitionDuration = 750;
      const start = () => {
        window.clearTimeout(timer);
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        timer = window.setTimeout(() => show(current + 1), Number(slider.dataset.autoplay || 4000));
      };
      const show = (index) => {
        const nextIndex = (index + slides.length) % slides.length;
        if (nextIndex === current || animating) {
          start();
          return;
        }
        window.clearTimeout(timer);
        animating = true;
        const previous = slides[current];
        const next = slides[nextIndex];

        next.classList.remove('is-active', 'is-leaving');
        next.classList.add('is-preparing');
        next.setAttribute('aria-hidden', 'false');
        previous.setAttribute('aria-hidden', 'true');
        void next.offsetWidth;

        current = nextIndex;
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === current));
        window.requestAnimationFrame(() => {
          previous.classList.remove('is-active');
          previous.classList.add('is-leaving');
          next.classList.remove('is-preparing');
          next.classList.add('is-active');
        });

        window.setTimeout(() => {
          previous.classList.remove('is-leaving');
          animating = false;
          start();
        }, transitionDuration);
      };
      dots.forEach((dot) => dot.addEventListener('click', () => show(Number(dot.dataset.slideTo))));
      let touchStartX = null;
      slider.addEventListener('touchstart', (event) => {
        touchStartX = event.touches[0]?.clientX ?? null;
        window.clearTimeout(timer);
      }, { passive: true });
      slider.addEventListener('touchend', (event) => {
        if (touchStartX === null) return;
        const touchEndX = event.changedTouches[0]?.clientX ?? touchStartX;
        const distance = touchEndX - touchStartX;
        if (Math.abs(distance) >= 40) show(current + (distance < 0 ? 1 : -1));
        touchStartX = null;
        if (Math.abs(distance) < 40) start();
      }, { passive: true });
      start();
    });
  };

  const initFAQ = () => {
    qa('.vellyn-faq').forEach((accordion) => {
      const items = qa('details', accordion);
      items.forEach((item) => item.addEventListener('toggle', () => {
        if (!item.open) return;
        items.forEach((other) => {
          if (other !== item) other.removeAttribute('open');
        });
      }));
    });
  };

  const initScrollCarousels = () => {
    qa('[data-scroll-carousel]').forEach((carousel) => {
      const track = q('[data-scroll-track]', carousel);
      const holder = q('[data-scroll-dots]', carousel);
      if (!track || !holder) return;
      const items = [...track.children];
      holder.innerHTML = items.map((_, i) => `<button type="button" data-scroll-to="${i}" aria-label="Show item ${i + 1}"></button>`).join('');
      const dots = qa('[data-scroll-to]', holder);
      const update = () => {
        const first = items[0];
        const step = first ? first.getBoundingClientRect().width + 16 : track.clientWidth;
        const index = Math.max(0, Math.min(items.length - 1, Math.round(track.scrollLeft / step)));
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      };
      dots.forEach((dot) => dot.addEventListener('click', () => items[Number(dot.dataset.scrollTo)]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })));
      track.addEventListener('scroll', update, { passive: true });
      update();
    });
  };

  const initProducts = () => {
    qa('[data-product-root]').forEach((root) => {
      const panels = qa('[data-media-panel]', root);
      const thumbs = qa('[data-media-thumb]', root);
      thumbs.forEach((thumb) => thumb.addEventListener('click', () => {
        const index = Number(thumb.dataset.mediaThumb);
        panels.forEach((panel, i) => { panel.hidden = i !== index; panel.classList.toggle('is-active', i === index); });
        thumbs.forEach((item, i) => item.classList.toggle('is-active', i === index));
      }));

      const input = q('[data-quantity-input]', root);
      const variant = q('[data-variant-select]', root);
      const label = q('[data-add-label]', root);
      const updateLabel = () => {
        if (!label || !input) return;
        const cents = Number(variant?.selectedOptions?.[0]?.dataset.price || root.querySelector('[name="id"]')?.dataset.price || 0);
        if (cents) label.textContent = `Add to Bag - ${money(cents * Math.max(1, Number(input.value) || 1))}`;
      };
      q('[data-quantity-minus]', root)?.addEventListener('click', () => { input.value = Math.max(1, Number(input.value) - 1); updateLabel(); });
      q('[data-quantity-plus]', root)?.addEventListener('click', () => { input.value = Math.max(1, Number(input.value) + 1); updateLabel(); });
      input?.addEventListener('change', updateLabel);
      variant?.addEventListener('change', updateLabel);

      const form = q('[data-product-form]', root);
      form?.addEventListener('submit', async (event) => {
        event.preventDefault();
        const submit = q('[type="submit"]', form);
        submit.disabled = true;
        const original = label?.textContent;
        if (label) label.textContent = 'Adding…';
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
          if (!response.ok) throw new Error('Unable to add item');
          await refreshCart();
          const drawer = q('[data-cart-drawer]');
          const overlay = q('[data-drawer-overlay]');
          setDrawer(drawer, overlay, true);
        } catch (_) {
          if (label) label.textContent = 'Please try again';
        } finally {
          submit.disabled = false;
          window.setTimeout(() => { if (label && original) label.textContent = original; }, 1200);
        }
      });
    });
  };

  const initPrivacy = () => {
    qa('[data-cookie-preferences]').forEach((button) => button.addEventListener('click', () => {
      if (window.Shopify?.customerPrivacy?.showPreferences) window.Shopify.customerPrivacy.showPreferences();
      else document.dispatchEvent(new CustomEvent('shopify:privacy:show'));
    }));

    const matchBannerToStorefront = () => {
      const banner = q('#shopify-pc__banner');
      if (!banner || banner.dataset.vellynStyled === 'true') return Boolean(banner);
      banner.dataset.vellynStyled = 'true';

      const message = q('.shopify-pc__banner__body p', banner);
      if (message) {
        message.innerHTML = 'We use cookies to improve your experience and help us reach you with relevant content. You can manage your preferences or accept all cookies. <a href="/policies/privacy-policy">Learn more</a>';
      }

      const decline = q('#shopify-pc__banner__btn-decline', banner);
      const manage = q('#shopify-pc__banner__btn-manage-prefs', banner);
      const accept = q('#shopify-pc__banner__btn-accept', banner);
      if (decline) decline.textContent = 'Essential Only';
      if (manage) manage.textContent = 'Manage Preferences';
      if (accept) accept.textContent = 'Accept All';
      return true;
    };

    if (!matchBannerToStorefront()) {
      const observer = new MutationObserver(() => {
        if (matchBannerToStorefront()) observer.disconnect();
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
      window.setTimeout(() => observer.disconnect(), 10000);
    }
  };

  const init = () => {
    initDrawers();
    initHero();
    initFAQ();
    initScrollCarousels();
    initProducts();
    initPrivacy();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
